import { isEmpty } from '@/shared/util';

import { createQuillListItem } from './builder.util';
import { MAX_QUILL_LIST_INDENT } from './list.constant';
import {
  GOOGLE_DOCS_INTERNAL_GUID_PATTERN,
  GOOGLE_DOCS_MARGIN_INDENT_PT,
  LIST_STYLE_TYPE_PATTERN,
  MARGIN_LEFT_STYLE_PATTERN,
} from './nested-list-clipboard.constant';
import { GoogleDocsMarginListBlock, QuillListType } from './nested-list-clipboard.model';
import {
  areNearbyListBlocks,
  cloneListItemContent,
  hasListItemContent,
  parseHtmlDocument,
} from './nested-list-clipboard-dom.util';

function parseMarginLeftPt(style: string): null | number {
  const pointMatch = style.match(/margin-left:\s*([\d.]+)\s*pt/i);

  if (pointMatch) {
    return Number.parseFloat(pointMatch[1]);
  }

  const pixelMatch = style.match(/margin-left:\s*([\d.]+)\s*px/i);

  if (pixelMatch) {
    // CSS px → pt at 96dpi (1px = 0.75pt) so indent steps match the 36pt constant.
    return Number.parseFloat(pixelMatch[1]) * 0.75;
  }

  return null;
}

export function hasGoogleDocsMarginIndentLists(html: string): boolean {
  if (GOOGLE_DOCS_INTERNAL_GUID_PATTERN.test(html) && MARGIN_LEFT_STYLE_PATTERN.test(html)) {
    return true;
  }

  const doc = parseHtmlDocument(html);
  const marginIndents = Array.from(doc.querySelectorAll('ul > li, ol > li'))
    .filter((listItem): listItem is HTMLLIElement => listItem instanceof HTMLLIElement)
    .map((listItem) => parseMarginLeftPt(listItem.getAttribute('style') ?? ''))
    .filter((marginLeftPt): marginLeftPt is number => marginLeftPt !== null)
    .map((marginLeftPt) => Math.floor(marginLeftPt / GOOGLE_DOCS_MARGIN_INDENT_PT));

  return new Set(marginIndents).size >= 2;
}

function getListTypeFromListStyleType(style: string): QuillListType {
  const match = style.match(LIST_STYLE_TYPE_PATTERN);

  if (!match || isEmpty(match[1])) {
    return 'bullet';
  }

  const listStyleType = match[1].trim().toLowerCase();

  if (listStyleType === 'disc' || listStyleType === 'circle' || listStyleType === 'square') {
    return 'bullet';
  }

  if (
    listStyleType.startsWith('decimal') ||
    listStyleType.startsWith('lower-') ||
    listStyleType.startsWith('upper-')
  ) {
    return 'ordered';
  }

  return 'bullet';
}

function cloneGoogleDocsMarginListItemContent(listItem: HTMLLIElement): DocumentFragment {
  const presentationParagraph = listItem.querySelector(':scope > p[role="presentation"]');

  if (presentationParagraph instanceof HTMLParagraphElement) {
    const fragment = document.createDocumentFragment();

    for (const child of Array.from(presentationParagraph.childNodes)) {
      fragment.appendChild(child.cloneNode(true));
    }

    return fragment;
  }

  return cloneListItemContent(listItem);
}

function getIndentFromMarginStyle(style: string): null | number {
  const marginLeftPt = parseMarginLeftPt(style);

  if (marginLeftPt === null) {
    return null;
  }

  return Math.min(Math.floor(marginLeftPt / GOOGLE_DOCS_MARGIN_INDENT_PT), MAX_QUILL_LIST_INDENT);
}

function parseGoogleDocsMarginListBlock(element: HTMLElement): GoogleDocsMarginListBlock | null {
  const listItems = Array.from(element.children).filter(
    (child): child is HTMLLIElement => child instanceof HTMLLIElement,
  );

  const firstListItemWithMargin = listItems.find(
    (listItem) => getIndentFromMarginStyle(listItem.getAttribute('style') ?? '') !== null,
  );

  if (!firstListItemWithMargin) {
    return null;
  }

  const style = firstListItemWithMargin.getAttribute('style') ?? '';

  return {
    element,
    indent: getIndentFromMarginStyle(style) ?? 0,
    listType: getListTypeFromListStyleType(style),
  };
}

function findGoogleDocsMarginListBlocks(root: ParentNode): GoogleDocsMarginListBlock[] {
  return Array.from(root.querySelectorAll('ul, ol'))
    .filter((element): element is HTMLElement => element instanceof HTMLElement)
    .map(parseGoogleDocsMarginListBlock)
    .filter((block): block is GoogleDocsMarginListBlock => block !== null);
}

function groupConsecutiveMarginListBlocks(
  blocks: GoogleDocsMarginListBlock[],
): GoogleDocsMarginListBlock[][] {
  if (isEmpty(blocks)) {
    return [];
  }

  const groups: GoogleDocsMarginListBlock[][] = [[blocks[0]]];

  for (let index = 1; index < blocks.length; index += 1) {
    const previousBlock = blocks[index - 1];
    const currentBlock = blocks[index];
    const isNearby = areNearbyListBlocks(previousBlock.element, currentBlock.element);

    if (isNearby) {
      groups[groups.length - 1].push(currentBlock);
      continue;
    }

    groups.push([currentBlock]);
  }

  return groups;
}

function convertGoogleDocsMarginListItem({
  fallbackIndent,
  fallbackListType,
  flattenedList,
  listItem,
}: {
  fallbackIndent: number;
  fallbackListType: QuillListType;
  flattenedList: HTMLOListElement;
  listItem: HTMLLIElement;
}): void {
  if (!hasListItemContent(listItem)) {
    return;
  }

  const style = listItem.getAttribute('style') ?? '';
  const indent = getIndentFromMarginStyle(style) ?? fallbackIndent;

  flattenedList.appendChild(
    createQuillListItem({
      content: cloneGoogleDocsMarginListItemContent(listItem),
      indent,
      listType: LIST_STYLE_TYPE_PATTERN.test(style)
        ? getListTypeFromListStyleType(style)
        : fallbackListType,
    }),
  );
}

function convertGoogleDocsMarginBlockGroup(group: GoogleDocsMarginListBlock[]): HTMLOListElement {
  const flattenedList = document.createElement('ol');

  for (const block of group) {
    for (const child of Array.from(block.element.children)) {
      if (!(child instanceof HTMLLIElement)) {
        continue;
      }

      convertGoogleDocsMarginListItem({
        fallbackIndent: block.indent,
        fallbackListType: block.listType,
        flattenedList,
        listItem: child,
      });
    }
  }

  return flattenedList;
}

function unwrapElement(element: HTMLElement): void {
  const parent = element.parentNode;

  if (!parent) {
    return;
  }

  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }

  element.remove();
}

export function convertGoogleDocsMarginIndentLists(root: ParentNode): boolean {
  const blocks = findGoogleDocsMarginListBlocks(root);

  if (isEmpty(blocks)) {
    return false;
  }

  let didConvert = false;
  const wrappersToUnwrap = new Set<HTMLElement>();

  for (const group of groupConsecutiveMarginListBlocks(blocks)) {
    const flattenedList = convertGoogleDocsMarginBlockGroup(group);
    const firstBlock = group[0]?.element;

    if (!firstBlock?.parentElement || isEmpty(Array.from(flattenedList.children))) {
      continue;
    }

    const parent = firstBlock.parentElement;

    parent.insertBefore(flattenedList, firstBlock);

    for (const block of group) {
      block.element.remove();
    }

    if (
      parent instanceof HTMLElement &&
      parent.tagName === 'B' &&
      GOOGLE_DOCS_INTERNAL_GUID_PATTERN.test(parent.id)
    ) {
      wrappersToUnwrap.add(parent);
    }

    didConvert = true;
  }

  for (const wrapper of wrappersToUnwrap) {
    unwrapElement(wrapper);
  }

  return didConvert;
}
